import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { Badge } from "@/components/ui/Badge";
import {
  Filter,
  Eye,
  Search,
  Plus,
  Edit,
  Trash2,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileDown,
} from "lucide-react";
import api from "../../lib/api";
import toast from "react-hot-toast";
import DeleteConfirmDialog from "@/components/ui/DeleteConfirmDialog";
import EditIcon from "@/components/icons/EditIcon";
import ViewIcon from "@/components/icons/ViewIcon";
import DeleteIcon from "@/components/icons/DeleteIcon";
import CloseIcon from "@/components/icons/CloseIcon";
import FileIcon from "@/components/icons/FileIcon";
import GenerateReportDialog from "@/components/reports/GenerateReportDialog";

const ReportsIndexPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    report: null,
  });
  const [generateDialog, setGenerateDialog] = useState({
    isOpen: false,
    reportId: null,
    reportName: "",
    phase: "confirm", // "confirm" | "processing"
  });
  const [isSearching, setIsSearching] = useState(false);
  const [generatingReportId, setGeneratingReportId] = useState(null);

  // Debounce search input
  useEffect(() => {
    if (search !== debouncedSearch) {
      setIsSearching(true);
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  // Fetch reports data
  const { data, isLoading, error } = useQuery(
    ["reports", page, debouncedSearch, statusFilter],
    async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter && { status: statusFilter }),
      });
      const response = await api.get(`/reports?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  const deleteMutation = useMutation((reportId) => api.delete(`/reports/${reportId}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(["reports"]);
      queryClient.invalidateQueries(["mine-types"]);
      queryClient.refetchQueries(["mine-types"]);
      toast.success("Report deleted successfully");
      setDeleteDialog({ isOpen: false, report: null });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete report");
    },
  });

  const generateReportMutation = useMutation(
    (reportId) => {
      setGeneratingReportId(reportId);
      return api.post(`/reports/${reportId}/generate`, {}, {
        timeout: 300000,
      });
    },
    {
      onSuccess: (response) => {
        const { data } = response.data;
        toast.success("Report generated successfully!");

        if (data?.download_url) {
          window.open(data.download_url, '_blank');
        }

        queryClient.invalidateQueries(["reports"]);
        setGeneratingReportId(null);
        setGenerateDialog({
          isOpen: false,
          reportId: null,
          reportName: "",
          phase: "confirm",
        });
      },
      onError: (error) => {
        let errorMessage = "Failed to generate report";

        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          errorMessage = "Report generation is taking longer than expected. Please check the server or try again later.";
        } else {
          errorMessage = error.response?.data?.message || error.message || errorMessage;
        }

        toast.error(errorMessage);
        console.error("Report generation error:", error);
        setGeneratingReportId(null);
        setGenerateDialog({
          isOpen: false,
          reportId: null,
          reportName: "",
          phase: "confirm",
        });
      },
    }
  );

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value === "all" ? "" : value);
    setPage(1);
  };

  const handleDeleteClick = (report) => {
    setDeleteDialog({
      isOpen: true,
      report: report,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.report) {
      deleteMutation.mutate(deleteDialog.report.id);
    }
  };

  const formatLabel = (value) => {
    if (!value) return "-";
    const text = String(value).replace(/_/g, " ").toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const handleEditClick = (reportId) => {
    navigate(`/reports/edit/${reportId}`);
  };
  const handleViewClick = (reportId) => {
    navigate(`/reports/view/${reportId}`);
  };
  const handleStackClick = (reportId) => {
    navigate(`/reports/stack/${reportId}`);
  };
  const handleEditValuesClick = (reportId) => {
    navigate(`/reports/edit-values/${reportId}`);
  };

  const handleGenerateReport = (reportId, reportName = "") => {
    setGenerateDialog({
      isOpen: true,
      reportId,
      reportName,
      phase: "confirm",
    });
  };

  const handleGenerateConfirm = () => {
    if (!generateDialog.reportId) return;
    setGenerateDialog((prev) => ({ ...prev, phase: "processing" }));
    generateReportMutation.mutate(generateDialog.reportId);
  };

  const handleGenerateDialogClose = () => {
    if (generateDialog.phase === "processing") return;
    setGenerateDialog({
      isOpen: false,
      reportId: null,
      reportName: "",
      phase: "confirm",
    });
  };

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "awaiting", label: "Awaiting" },
    { value: "awaiting_field_engineer", label: "Awaiting Field Engineer" },
    { value: "started_field_engineer", label: "Started Field Engineer" },
    { value: "uploaded_field_engineer", label: "Uploaded Field Engineer" },
    { value: "failed_peer_review", label: "Failed Peer Review" },
    { value: "failed_site_review", label: "Failed Site Review" },
    { value: "failed_director_review", label: "Failed Director Review" },
    { value: "awaiting_peer_reviewer", label: "Awaiting Peer Reviewer" },
    { value: "started_peer_review", label: "Started Peer Review" },
    { value: "uploaded_peer_review", label: "Uploaded Peer Review" },
    { value: "awaiting_site_reviewer", label: "Awaiting Site Reviewer" },
    { value: "started_site_review", label: "Started Site Review" },
    { value: "uploaded_site_review", label: "Uploaded Site Review" },
    { value: "awaiting_director_reviewer", label: "Awaiting Director Reviewer" },
    { value: "started_director_review", label: "Started Director Review" },
    { value: "uploaded_director_review", label: "Uploaded Director Review" },
    { value: "completed", label: "Completed" },
  ];

  const getLabel = (value) => {
    const option = statusOptions.find((opt) => opt.value === value);
    return option ? option.label : "All Status";
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600">Failed to load reports</p>
          <Button onClick={() => window.location.reload()} className="mt-2">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="">
        {/* Header */}
        <div className="flex flex-col gap-3 slide-up lg:flex-row lg:items-center lg:justify-between">
          <div className="slide-up">
            <Link to="/reports/create" className="block w-full sm:w-auto">
              <Button className="w-full text-sm gradient-primary hover:bg-red-900 sm:w-auto sm:text-base">
                <FileIcon className="w-5 h-5 mr-2" />
                Add Report
              </Button>
            </Link>
          </div>
          {/* Filters */}
          <div className="flex flex-col w-full gap-3 slide-up lg:w-auto">
            <div className="flex flex-col w-full gap-3 sm:flex-row sm:items-end lg:justify-end">
              <div className="flex-1 min-w-0 sm:max-w-xs">
                <div className="relative">
                  <Input
                    placeholder="Search reports..."
                    value={search}
                    onChange={handleSearch}
                    className="w-full pr-10 border-primary/20 focus:border-primary/40"
                  />
                  <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none right-3 top-1/2" />
                </div>
              </div>
              <div className="relative z-30 w-full sm:w-48">
                <Select value={statusFilter || "All Status"} onValueChange={handleStatusFilter}>
                  <SelectTrigger className="w-full text-sm sm:text-base">
                    <SelectValue>
                      {getLabel(statusFilter)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="z-50 mt-1 overflow-y-auto text-gray-400 shadow-lg bg-navy-100 max-h-60 hide-scrollbar">
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("");
                  }}
                  className="w-full text-sm hover:bg-red-900 sm:w-auto sm:text-base"
                >
                  <CloseIcon className="w-5 h-5 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-8 h-8 mx-auto mb-4 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
              <p className="text-gray-600">Loading reports...</p>
            </div>
          </div>
        ) : (
          <div className="slide-up backdrop-blur-sm">
            <div className="overflow-x-auto custom-scrollbar">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-200 whitespace-nowrap hover:bg-gray-50/50">
                    <TableHead className="sticky md:sticky md:left-0 md:top-0">Report Name</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Visit Date</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isSearching ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-12 text-center">
                        <div className="text-center">
                          <LoadingSpinner size="lg" />
                          <p className="mt-4 text-gray-600">Searching reports...</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : !data?.data || data.data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-12 text-center">
                        <div className="text-gray-500">
                          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No Reports Found</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    // Ensuring unique keys by checking report.id
                    [...new Set(data.data.map((report) => report.id))].map((id) => {
                      const report = data.data.find((r) => r.id === id);
                      return (
                        <TableRow key={id}>
                          <TableCell className="sticky max-w-64 whitespace-nowrap md:sticky md:left-0 md:top-0">
                            <div className="p-4 my-2 mr-2 rounded-t-lg bg-navy-100 text-gray-0 ">
                              <div className="flex items-center gap-3 break-words truncate">
                                {report.report_name}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-64">
                            <div className="p-4 m-2 truncate rounded-t-lg bg-navy-100 text-gray-0 ">
                              {report.client_name
                                ? report.client_name.length > 22
                                  ? `${report.client_name.substring(0, 22)}...`
                                  : report.client_name
                                : "client name not found"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="p-4 m-2 rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0">
                              {formatLabel(report.status)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="p-4 m-2 text-sm rounded-t-lg whitespace-nowrap bg-navy-100 text-gray-0">
                              {new Date(report.visited_date).toLocaleDateString()}
                            </div>
                          </TableCell>
                          <TableCell className="w-4 text-center">
                            <div className="p-2 my-2 ml-2 rounded-t-lg bg-navy-100 text-gray-0">
                              <div className="flex items-center justify-center gap-2 sm:flex-row sm:gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditValuesClick(report.id)}
                                  className="w-full h-8 text-xs uppercase transition-transform duration-200 text-gray-0 sm:p-4 hover:text-red-500 sm:h-9 sm:w-auto sm:text-sm hover:scale-110"
                                >
                                  Edit Values
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStackClick(report.id)}
                                  className="w-full h-8 text-xs uppercase transition-transform duration-200 text-gray-0 sm:p-4 hover:text-red-500 sm:h-9 sm:w-auto sm:text-sm hover:scale-110"
                                >
                                  Stack
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleGenerateReport(report.id, report.report_name)}
                                  disabled={generatingReportId !== null}
                                  className="w-full h-8 text-xs uppercase transition-transform duration-200 text-gray-0 sm:p-4 hover:text-red-500 sm:h-9 sm:w-auto sm:text-sm hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Generate Report Document"
                                >
                                  {generatingReportId === report.id ? (
                                    <div className="w-4 h-4 border-2 border-gray-400 rounded-full border-t-transparent animate-spin"></div>
                                  ) : (
                                    <FileDown className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewClick(report.id)}
                                  className="w-full h-8 text-xs uppercase text-gray-0 hover:text-red-600 sm:h-9 sm:w-auto sm:text-sm"
                                >
                                  <ViewIcon className="w-5 h-5 text-white transition-transform duration-200 hover:text-red-500 hover:scale-110" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditClick(report.id)}
                                  className="w-full h-8 text-xs text-gray-0 hover:text-red-600 sm:h-9 sm:w-auto sm:text-sm"
                                >
                                  <EditIcon className="w-5 h-5 text-white transition-transform duration-200 hover:text-red-500 hover:scale-110" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteClick(report)}
                                  className="w-full h-8 text-xs hover:text-red-600 sm:h-9 sm:w-auto sm:text-sm"
                                >
                                  <DeleteIcon className="w-5 h-5 text-red-500 transition-transform duration-200 hover:scale-110" />
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        {/* Pagination */}
        <div className="flex flex-col pr-0 border-t border-b slide-up border-gray-0 sm:flex-row sm:items-center sm:justify-between sm:pr-12">
          <div className="py-4 text-sm text-center text-gray-400 uppercase sm:text-left">
            <span className="block sm:inline">
              Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, data?.pagination?.total || 0)}{" "}
              of {data?.pagination?.total || 0} results
            </span>
          </div>
          <div className="flex items-center justify-center w-full sm:w-auto">
            <Button
              variant="outline"
              className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-r border-gray-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1 text-red-500 sm:mr-2" />
              <span className="hidden sm:inline">PREVIOUS</span>
              <span className="sm:hidden">PREV</span>
            </Button>
            <Button
              variant="outline"
              className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-r border-gray-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
            >
              {page}
            </Button>
            <Button
              variant="outline"
              className="flex h-auto min-h-[44px] flex-1 items-center justify-center rounded-none rounded-b-none border-0 p-3 text-xs font-normal uppercase text-gray-0 hover:scale-100 sm:min-h-[52px] sm:flex-none sm:p-5"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= data?.pagination?.pages}
            >
              <span className="hidden sm:inline">NEXT</span>
              <span className="sm:hidden">NEXT</span>
              <ChevronRight className="w-4 h-4 ml-1 text-red-500 sm:ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, report: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Report"
        userName={deleteDialog.report?.report_name || ""}
        isLoading={deleteMutation.isLoading}
      />

      <GenerateReportDialog
        isOpen={generateDialog.isOpen}
        phase={generateDialog.phase}
        reportName={generateDialog.reportName}
        intent="generate"
        onClose={handleGenerateDialogClose}
        onConfirm={handleGenerateConfirm}
      />
    </div>
  );
};

export default ReportsIndexPage;
